import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AddIcon from "@mui/icons-material/Add";
import InboxIcon from "@mui/icons-material/Inbox";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { selectUser } from "../../../features/userSlice";
import { normalizeDate } from "../../../utilities";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";
import {
  getForms,
  getDrafts,
  getAllRecognitions,
  getWindowFormDefinitions,
} from "../../../components/Forms/api";
import { fetchServiceRequests } from "../../../components/WindowDoor/ServiceRequests/api";
import {
  fetchRemodelLeads,
  remodelLeadPdfUrl,
} from "../../../components/Forms/Remodel/api";
import { fetchMyFeatureRequests } from "../../FeatureRequests/featureRequestsApi";
import { statusLabel } from "../../FeatureRequests/featureRequestStatus";

/*
  One list of everything the user has submitted, across every form system.

  Each source below fetches through that system's OWN client API helper —
  never a hand-built URL — and normalizes to:
    { key, source, date, title, subtitle, status, tone, action }
  where `action` is { label, to } (router link) or { label, href } (new tab).

  New forms add a source here and a card in components/Forms/formsCatalog.js
  — see "Adding a new form" in CLAUDE.md. Order Problem reports are the one
  deliberate absence: they're anonymous, so there is nothing to list.
*/

const SOURCES = [
  { id: "rfq", label: "RFQ Forms" },
  { id: "service", label: "Service Requests" },
  { id: "remodel", label: "Remodel Leads" },
  { id: "feature", label: "Feature Requests" },
  { id: "recognition", label: "Recognitions" },
];

// Status chip color families. Keep it coarse — the source pages carry the
// full status vocabulary; this list only needs "how is it going" at a glance.
const TONES = {
  emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  red: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  slate: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
};

// Window/door service request statuses → tone (see serviceRequests.js STATUS).
const SERVICE_TONES = {
  Submitted: "sky",
  "Homeowner Contacted": "sky",
  "Job Quoted": "amber",
  "Parts Ordered": "sky",
  "Job Completed - Billing Required": "amber",
  "Service Completed": "emerald",
  "Taken Off": "red",
  Inactive: "slate",
};

const FEATURE_TONES = {
  completed: "emerald",
  declined: "slate",
};

const asArray = (value) => (Array.isArray(value) ? value : []);

// Several legacy helpers are setter-style; capture the setter's value. Their
// own .catch swallows network errors, so a failure resolves to [].
const viaSetter = async (fn, ...args) => {
  let out = [];
  await fn((data) => {
    out = asArray(data);
  }, ...args);
  return out;
};

const rfqLabel = (row) => {
  if (row?.form_name) return row.form_name;
  switch (row?.form_id) {
    case 6:
      return "Interior Door";
    case 11:
      return "Exterior Door";
    default:
      return "RFQ Form";
  }
};

const StatusChip = ({ status, tone }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      TONES[tone] || TONES.slate
    }`}
  >
    {status}
  </span>
);

const RowAction = ({ action }) => {
  if (action?.to) {
    return (
      <Button component={Link} to={action.to} size="small" variant="outlined">
        {action.label}
      </Button>
    );
  }
  if (action?.href) {
    return (
      <Button
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        variant="outlined"
        endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
      >
        {action.label}
      </Button>
    );
  }
  return null;
};

const formatDate = (date) => (date ? normalizeDate(new Date(date)) : "—");

const MySubmissions = () => {
  const user = useSelector(selectUser);

  const [rows, setRows] = useState(null);
  const [failedSources, setFailedSources] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!user?.userID && !user?.email) return;
    let cancelled = false;

    const load = async () => {
      const failed = [];
      const guard = async (sourceId, fetcher) => {
        try {
          return await fetcher();
        } catch (err) {
          failed.push(sourceId);
          return [];
        }
      };

      const [
        windowDefinitions,
        submittedForms,
        draftForms,
        serviceRequests,
        remodelLeads,
        featureRequests,
        recognitions,
      ] = await Promise.all([
        guard("rfq", () => viaSetter(getWindowFormDefinitions)),
        guard("rfq", () => viaSetter(getForms, user)),
        guard("rfq", () => viaSetter(getDrafts, user)),
        guard("service", () =>
          fetchServiceRequests({ scope: "mine", user }).then(asArray)
        ),
        guard("remodel", () =>
          user?.email
            ? fetchRemodelLeads(user.email).then(asArray)
            : Promise.resolve([])
        ),
        guard("feature", () =>
          user?.email
            ? fetchMyFeatureRequests(user.email).then(asArray)
            : Promise.resolve([])
        ),
        guard("recognition", () => viaSetter(getAllRecognitions)),
      ]);

      // Window-form entries get a real detail page; other RFQ entries get
      // their PDF — the same split My Forms makes.
      const windowFormIds = new Set(
        windowDefinitions
          .map((definition) =>
            definition?.form_id !== undefined
              ? Number(definition.form_id)
              : null
          )
          .filter(Boolean)
      );

      const collected = [
        ...submittedForms.map((row) => ({
          key: `rfq-${row._id}`,
          source: "rfq",
          date: row.entry_date || row.last_save || null,
          title: rfqLabel(row),
          subtitle: [row.customer, row.job].filter(Boolean).join(" — "),
          status: "Submitted",
          tone: "emerald",
          action: windowFormIds.has(Number(row.form_id))
            ? { label: "View", to: `/windowdoor/sales/windowforms/${row._id}` }
            : { label: "View PDF", href: `/api/forms/pdf/?id=${row._id}` },
        })),
        ...draftForms.map((row) => ({
          key: `rfq-draft-${row._id}`,
          source: "rfq",
          date: row.entry_date || row.last_save || null,
          title: rfqLabel(row),
          subtitle: [row.customer, row.job].filter(Boolean).join(" — "),
          status: "Draft",
          tone: "amber",
          action: { label: "Continue in My Forms", to: "/forms/viewall" },
        })),
        ...serviceRequests.map((row) => ({
          key: `service-${row._id}`,
          source: "service",
          date: row.entry_date || null,
          title: `Service Request #${row.entry_number || "?"}`,
          subtitle:
            row.homeowner_name ||
            (row.home_occupied === false ? "Home Not Occupied" : ""),
          status: row.status || "Submitted",
          tone: SERVICE_TONES[row.status] || "slate",
          action: {
            label: "View",
            to: `/windowdoor/my-service-requests/${row._id}`,
          },
        })),
        ...remodelLeads.map((row) => ({
          key: `remodel-${row._id}`,
          source: "remodel",
          date: row.entry_date || null,
          title: `Remodel Lead${
            row.customer_name ? ` — ${row.customer_name}` : ""
          }`,
          subtitle: row.address || "",
          status: "Sent to Remodel Team",
          tone: "emerald",
          action: { label: "View PDF", href: remodelLeadPdfUrl(row._id) },
        })),
        ...featureRequests.map((row) => ({
          key: `feature-${row._id}`,
          source: "feature",
          date: row.createdAt || null,
          title: row.title || "Feature request",
          subtitle: row.type || "",
          status: statusLabel(row.status),
          tone: FEATURE_TONES[row.status] || "sky",
          action: { label: "View", to: "/forms/feature-request" },
        })),
        // The recognitions endpoint returns everything; keep only the ones
        // this user submitted. Legacy rows store submit_user as a plain name
        // string (or nothing) — skip silently rather than guess.
        ...recognitions
          .filter(
            (row) => row.submit_user && user?.name && row.submit_user === user.name
          )
          .map((row) => ({
            key: `recognition-${row._id}`,
            source: "recognition",
            date: row.date_entered || null,
            title: `Recognized ${
              asArray(row.employees)
                .map((employee) => employee?.name)
                .filter(Boolean)
                .join(", ") ||
              row.employee ||
              "a coworker"
            }`,
            subtitle: asArray(row.value).join(", "),
            status: "Submitted",
            tone: "emerald",
            action: null,
          })),
      ];

      if (cancelled) return;
      collected.sort(
        (a, b) =>
          (b.date ? new Date(b.date).getTime() : 0) -
          (a.date ? new Date(a.date).getTime() : 0)
      );
      setRows(collected);
      setFailedSources([...new Set(failed)]);
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userID, user?.email, user?.name]);

  const counts = useMemo(() => {
    const bySource = {};
    (rows || []).forEach((row) => {
      bySource[row.source] = (bySource[row.source] || 0) + 1;
    });
    return bySource;
  }, [rows]);

  const visible = useMemo(() => {
    let list = rows || [];
    if (sourceFilter !== "all") {
      list = list.filter((row) => row.source === sourceFilter);
    }
    const trimmed = query.trim().toLowerCase();
    if (trimmed) {
      list = list.filter((row) =>
        [row.title, row.subtitle, row.status]
          .join(" ")
          .toLowerCase()
          .includes(trimmed)
      );
    }
    return list;
  }, [rows, sourceFilter, query]);

  const sourceLabel = (id) => SOURCES.find((s) => s.id === id)?.label || id;

  const chip = (id, label, count) => {
    const active = sourceFilter === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => setSourceFilter(id)}
        aria-pressed={active}
        className={`inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
          active
            ? "border-transparent bg-sky-600 text-white shadow-sm"
            : "app-surface text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]"
        }`}
      >
        {label}
        {typeof count === "number" && (
          <span
            className={`tabular-nums text-xs ${
              active ? "text-white/80" : "app-muted"
            }`}
          >
            {count}
          </span>
        )}
      </button>
    );
  };


  return (
    <PageShell size="lg">
      <PageHeader
        icon={FactCheckIcon}
        tone="sky"
        eyebrow="Forms"
        title="My Submissions"
        description="Everything you've submitted, across every form. Order Problem reports are anonymous, so they can't be listed."
        actions={
          <Button
            component={Link}
            to="/forms"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Submit a Form
          </Button>
        }
      />

      {failedSources.length > 0 && (
        <div
          role="status"
          className="mb-4 rounded-xl bg-amber-500/15 px-4 py-3 text-sm text-amber-800 dark:text-amber-200"
        >
          Couldn't load: {failedSources.map(sourceLabel).join(", ")}. The rest
          of the list is complete.
        </div>
      )}

      <div className="space-y-3">
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label="Filter by form"
        >
          {chip("all", "All", rows ? rows.length : undefined)}
          {SOURCES.map((source) =>
            chip(source.id, source.label, counts[source.id] || 0)
          )}
        </div>

        <TextField
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search your submissions…"
          inputProps={{ "aria-label": "Search your submissions" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className="mt-5">
        {!rows ? (
          <div className="app-card rounded-2xl border">
            <LoadingState label="Gathering your submissions…" />
          </div>
        ) : visible.length === 0 ? (
          <div className="app-card rounded-2xl border">
            {rows.length === 0 ? (
              <EmptyState
                icon={InboxIcon}
                title="You haven't submitted anything yet"
                description="Start with Submit a Form — whatever you send shows up here with its status."
                action={
                  <Button
                    component={Link}
                    to="/forms"
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Submit a Form
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={SearchOffIcon}
                title="Nothing matches that filter."
                description="Try another form type or a different search."
                compact
              />
            )}
          </div>
        ) : (
          <>
            {/* Phones: one card per submission. */}
            <ul className="space-y-3 md:hidden">
              {visible.map((row) => (
                <li key={row.key} className="app-card rounded-2xl border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold leading-snug">{row.title}</p>
                      <p className="app-muted mt-0.5 text-xs">
                        {sourceLabel(row.source)} · {formatDate(row.date)}
                      </p>
                    </div>
                    <StatusChip status={row.status} tone={row.tone} />
                  </div>
                  {row.subtitle && (
                    <p className="mt-2 break-words text-sm">{row.subtitle}</p>
                  )}
                  {row.action && (
                    <div className="mt-3 flex justify-end">
                      <RowAction action={row.action} />
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Tablets and up: a table. */}
            <div className="app-card hidden overflow-hidden rounded-2xl border md:block">
              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Form</th>
                      <th>Details</th>
                      <th>Status</th>
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.key}>
                        <td className="whitespace-nowrap tabular-nums">
                          {formatDate(row.date)}
                        </td>
                        <td>
                          <span className="font-medium">{row.title}</span>
                          <span className="app-muted block text-xs">
                            {sourceLabel(row.source)}
                          </span>
                        </td>
                        <td>
                          {row.subtitle || <span className="app-muted">—</span>}
                        </td>
                        <td>
                          <StatusChip status={row.status} tone={row.tone} />
                        </td>
                        <td className="whitespace-nowrap text-right">
                          <RowAction action={row.action} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default MySubmissions;
